import { Logger } from '../devtools/logger';
import { createScheduler } from '../scheduler/scheduler';

const createIntervalTracker = () => {
  const intervalCounts = new Map<number, number>();

  let shortestInterval: number | null = null;

  const getShortestInterval = () => shortestInterval;

  return {
    getShortestInterval,
    add: (interval: number) => {
      intervalCounts.set(interval, (intervalCounts.get(interval) ?? 0) + 1);

      if (!shortestInterval || interval < shortestInterval) {
        shortestInterval = interval;
      }
    },
    remove: (interval: number) => {
      const count = intervalCounts.get(interval);

      if (count && count > 1) {
        intervalCounts.set(interval, count - 1);
      } else {
        intervalCounts.delete(interval);

        // Recalculate shortest interval only if we removed the last subscriber
        // with that interval
        if (interval === shortestInterval) {
          shortestInterval =
            intervalCounts.size > 0 ? Math.min(...intervalCounts.keys()) : null;
        }
      }
    },
  };
};

type IntervalTracker = ReturnType<typeof createIntervalTracker>;

export const createPollManager = ({
  logger,
  onPoll,
}: {
  logger?: Logger;
  onPoll: (instanceKey: string) => void;
}) => {
  const pollScheduler = createScheduler();

  const entityIntervals = new Map<string, IntervalTracker>();

  const schedulePoll = (instanceKey: string, interval: number) => {
    const execute = () => {
      onPoll(instanceKey);

      pollScheduler.queue(instanceKey, interval, execute);
    };

    pollScheduler.queue(instanceKey, interval, execute);
  };

  return {
    getAllStatuses: () => {
      const result: Record<
        string,
        {
          interval: number;
          scheduledAt: number;
          executeAt: number;
        }
      > = {};
      for (const [instanceKey, tracker] of entityIntervals) {
        const interval = tracker.getShortestInterval();
        const schedulerStatus = pollScheduler.getStatus(instanceKey);

        if (!schedulerStatus || interval == null) {
          continue;
        }

        result[instanceKey] = {
          interval,
          scheduledAt: schedulerStatus?.scheduledAt ?? null,
          executeAt: schedulerStatus?.executeAt ?? null,
        };
      }
      return result;
    },
    register: (instanceKey: string, interval: number) => {
      const registerLogger = logger?.withTags({ instanceKey, interval });

      if (interval <= 0) {
        registerLogger?.warn('invalid interval, skipping tracking');

        return () => {};
      }

      let tracker = entityIntervals.get(instanceKey);

      if (!tracker) {
        tracker = createIntervalTracker();
        entityIntervals.set(instanceKey, tracker);
      }

      const previousInterval = tracker.getShortestInterval();

      tracker.add(interval);

      if (!previousInterval || interval < previousInterval) {
        registerLogger?.debug('scheduling poll');
        schedulePoll(instanceKey, interval);
      }

      return () => {
        const currentShortestInterval = tracker.getShortestInterval();

        tracker.remove(interval);

        const nextShortestInterval = tracker.getShortestInterval();

        if (nextShortestInterval === null) {
          registerLogger?.debug('no remaining intervals, clearing poll');

          pollScheduler.clear(instanceKey);
          entityIntervals.delete(instanceKey);
        } else if (nextShortestInterval !== currentShortestInterval) {
          const rescheduleLogger = registerLogger?.withTags({
            pollInterval: nextShortestInterval,
          });

          rescheduleLogger?.debug(
            'rescheduling poll to next shortest interval'
          );

          schedulePoll(instanceKey, nextShortestInterval);
        }
      };
    },
  };
};

export type PollManager = ReturnType<typeof createPollManager>;
